import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Linkedin, Instagram, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { submitContact } from "../../lib/apiClient";
import { PROFILE } from "../../data";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success("Message sent — I'll be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "rounded-sm bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-berry focus-visible:border-berry h-12";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-36 bg-navy-deep text-white">
      <div className="mx-auto max-w-7xl section-x grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="label-caps text-berry-light mb-4">Contact</p>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight">
            Let's build something
            <br />
            <span className="italic text-berry-light">worth measuring.</span>
          </h2>
          <p className="mt-6 text-white/60 leading-relaxed max-w-md">
            Have a reporting challenge, a cash-flow question, or a project in mind?
            Send a note and I'll get back to you.
          </p>

          <div className="mt-12 space-y-5">
            <a
              href={`mailto:${PROFILE.email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-4 text-white/80 hover:text-berry-light transition-colors duration-200"
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-sm bg-white/10 border border-white/15">
                <Mail size={18} />
              </span>
              {PROFILE.email}
            </a>
            <div className="flex items-center gap-4 text-white/80">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-sm bg-white/10 border border-white/15">
                <MapPin size={18} />
              </span>
              {PROFILE.location}
            </div>
            <a
              href={PROFILE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin-link"
              className="flex items-center gap-4 text-white/80 hover:text-berry-light transition-colors duration-200"
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-sm bg-white/10 border border-white/15">
                <Linkedin size={18} />
              </span>
              LinkedIn
            </a>
            <a
              href={PROFILE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-link"
              className="flex items-center gap-4 text-white/80 hover:text-berry-light transition-colors duration-200"
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-sm bg-white/10 border border-white/15">
                <Instagram size={18} />
              </span>
              Instagram
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <motion.form
            data-testid="contact-form"
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white/[0.03] border border-white/10 rounded-sm p-8 lg:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="label-caps text-white/50 block mb-2">Name</label>
                <Input data-testid="contact-name" maxLength={100} value={form.name} onChange={set("name")} placeholder="Jane Doe" className={inputCls} />
              </div>
              <div>
                <label className="label-caps text-white/50 block mb-2">Email</label>
                <Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" className={inputCls} />
              </div>
            </div>
            <div className="mt-5">
              <label className="label-caps text-white/50 block mb-2">Subject</label>
              <Input data-testid="contact-subject" maxLength={150} value={form.subject} onChange={set("subject")} placeholder="What's this about?" className={inputCls} />
            </div>
            <div className="mt-5">
              <label className="label-caps text-white/50 block mb-2">Message</label>
              <Textarea data-testid="contact-message" maxLength={5000} value={form.message} onChange={set("message")} placeholder="Tell me a little about your project..." className={`${inputCls} h-auto min-h-[140px] py-3`} />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={loading}
              className="mt-8 w-full sm:w-auto bg-berry hover:bg-berry-light disabled:opacity-60 text-white px-8 py-3.5 rounded-sm transition-colors duration-200 flex items-center justify-center gap-2 hover:scale-[1.02]"
            >
              {loading ? (
                <><Loader2 size={18} className="animate-spin" /> Sending...</>
              ) : sent ? (
                <><CheckCircle2 size={18} /> Sent</>
              ) : (
                "Send message"
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
